import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  UserIcon,
  UserGroupIcon,
  GlobeAltIcon,
  ChartBarIcon,
  CogIcon,
  SunIcon,
  MoonIcon,
  CpuChipIcon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  LinkIcon,
  CreditCardIcon,
  HeartIcon,
  Cog6ToothIcon,
  UserPlusIcon,
  Squares2X2Icon,
  ChevronRightIcon,
  SparklesIcon,
  ArrowPathIcon,
  CodeBracketIcon,
  ShieldCheckIcon,
  IdentificationIcon,
  ComputerDesktopIcon,
  PlayIcon,
  SignalIcon,
  ClipboardDocumentListIcon,
  ScaleIcon,
  BellAlertIcon,
  KeyIcon,
  UserCircleIcon,
  ChevronDownIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useViewingAs } from '../contexts/ViewingAsContext';
import { api } from '../services/api';
import { WingrLockup } from './WingrMascot';

// Snapchat ghost icon — used for the Snapchat nav item
const SnapGhostIcon = ({ className = 'h-5 w-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.024.015.06.029.12.029.307-.007.706-.222 1.074-.422.3-.168.6-.33.867-.33.195 0 .398.046.577.138.698.352.696 1.08.695 1.18 0 .403-.126.726-.406.973-.198.18-.434.33-.669.479-.36.222-.733.452-.941.762-.065.097-.082.19-.056.292l.815 2.516c.352 1.093-.213 1.93-1.352 2.157-.186.039-.378.059-.567.059a4.063 4.063 0 0 1-1.556-.306c-.466-.196-.914-.452-1.346-.7-.506-.294-1.003-.583-1.48-.736-.317-.104-.655-.148-.988-.148-.33 0-.661.044-.98.148-.48.154-.982.443-1.49.738-.432.248-.88.504-1.348.7A4.071 4.071 0 0 1 7.26 17.8c-.189 0-.381-.02-.567-.059-1.14-.227-1.704-1.064-1.352-2.157l.815-2.516c.026-.102.009-.195-.056-.292-.208-.31-.581-.54-.94-.762-.236-.15-.472-.3-.67-.479-.28-.247-.406-.57-.406-.973-.001-.1-.003-.828.694-1.18.179-.092.382-.138.577-.138.267 0 .567.162.867.33.367.2.764.415 1.074.422.061 0 .098-.014.12-.03a9.68 9.68 0 0 1-.03-.51l-.003-.06c-.104-1.628-.23-3.654.299-4.847C7.857 1.07 11.214.793 12.206.793z"/>
  </svg>
);

// ── ViewingAs switcher (superadmin only) ─────────────────────────────────────

function ViewingAsSwitcher() {
  const { viewingAs, loading, error, switchToUser, clearViewingAs } = useViewingAs();
  const [open, setOpen]           = useState(false);
  const [users, setUsers]         = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [search, setSearch]       = useState('');
  const dropdownRef               = React.useRef(null);

  // Load users when dropdown opens
  React.useEffect(() => {
    if (!open) return;
    setUsersLoading(true);
    api.get('/admin/users?limit=200')
      .then(r => setUsers(r.data?.users || r.data || []))
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, [open]);

  // Close on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q || u.username?.toLowerCase().includes(q) || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const select = async (u) => {
    setOpen(false);
    setSearch('');
    await switchToUser(u);
    // Force a page reload so all data re-fetches with the new token
    window.location.reload();
  };

  const clear = () => {
    clearViewingAs();
    window.location.reload();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {viewingAs ? (
        // Active banner
        <div className="flex items-center gap-1.5 rounded-full bg-orange-100 dark:bg-orange-900/40 border border-orange-300 dark:border-orange-700 px-3 py-1 text-xs font-medium text-orange-800 dark:text-orange-300">
          <UserCircleIcon className="w-4 h-4" />
          <span>Viewing as <strong>{viewingAs.username}</strong></span>
          <button
            onClick={clear}
            title="Exit view-as mode"
            className="ml-1 hover:text-orange-600 dark:hover:text-orange-100 transition-colors"
          >
            <XCircleIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        // Dropdown trigger
        <button
          onClick={() => setOpen(v => !v)}
          disabled={loading}
          className="flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-600 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <UserCircleIcon className="w-4 h-4" />
          {loading ? 'Switching…' : 'View as user'}
          <ChevronDownIcon className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      )}

      {error && (
        <p className="absolute top-8 left-0 text-xs text-red-600 bg-white dark:bg-gray-800 rounded shadow px-2 py-1 z-50">
          {error}
        </p>
      )}

      {open && !viewingAs && (
        <div className="absolute right-0 top-9 z-50 w-72 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
          <div className="p-2 border-b border-gray-100 dark:border-gray-800">
            <input
              autoFocus
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-3 py-1.5 text-sm outline-none focus:border-indigo-400"
              placeholder="Search users…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {usersLoading ? (
              <p className="px-4 py-3 text-sm text-gray-400">Loading…</p>
            ) : !filtered.length ? (
              <p className="px-4 py-3 text-sm text-gray-400">No users found</p>
            ) : (
              filtered.map(u => (
                <button
                  key={u._id}
                  onClick={() => select(u)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <UserCircleIcon className="w-5 h-5 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate">{u.username}</div>
                    <div className="text-xs text-gray-500 truncate">{u.name || u.email || u.role}</div>
                  </div>
                  <span className={`ml-auto shrink-0 text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    u.status === 'active'
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                  }`}>{u.status || 'user'}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [automationsDrawerOpen, setAutomationsDrawerOpen] = useState(false);
  const [vpsDrawerOpen, setVpsDrawerOpen] = useState(false);
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [accountsCount, setAccountsCount] = useState(0);
  const [conversationsRemaining, setConversationsRemaining] = useState(0);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
    // Automations continue running - they're server-side processes
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!authLoading && user?.role === 'va') {
      navigate('/va', { replace: true });
    }
  }, [authLoading, user, navigate]);

  // Join user room for per-user socket events (scraper progress, mass task updates, etc.) on all dashboard routes
  useEffect(() => {
    if (!socket || !isAuthenticated || !user) return;
    const uid = user._id || user.id;
    if (uid) socket.emit('join-user-room', uid);
  }, [socket, isAuthenticated, user]);

  // Fleet size for nav limit badge — single countDocuments, not full GET /accounts.
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    let inFlight = false;
    const fetchAccountCount = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const response = await api.get('/accounts/count');
        setAccountsCount(response.data?.total ?? 0);
      } catch (error) {
        console.error('Error fetching account count:', error);
      } finally {
        inFlight = false;
      }
    };

    setConversationsRemaining(user.billing?.conversationsRemaining || 0);
    fetchAccountCount();
    const interval = setInterval(fetchAccountCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  const isAdmin      = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin';
  const features = user?.features || {};

  // Automation items that will be in the drawer — filtered by feature flags
  const automationItems = [
    ...(isAdmin || features.canUseMassDM ? [{ name: 'Mass DMs', href: '/dashboard/mass-dms', icon: ChatBubbleLeftRightIcon }] : []),
    ...(isAdmin || features.canUseMassLike ? [{ name: 'Mass Likes', href: '/dashboard/mass-likes', icon: HeartIcon }] : []),
    ...(isAdmin || features.canUseMassTasks ? [{ name: 'Mass Tasks', href: '/dashboard/mass-tasks', icon: Cog6ToothIcon }] : []),
    ...(isAdmin || features.canUseMassTasks ? [{ name: 'Update Accounts', href: '/dashboard/update-accounts', icon: ArrowPathIcon }] : []),
    ...(isAdmin ? [{ name: 'Mass Follow', href: '/dashboard/mass-follows', icon: UserPlusIcon }] : []),
    ...(isAdmin ? [{ name: 'Auto Comments', href: '/dashboard/auto-comments', icon: ChatBubbleLeftRightIcon }] : []),
  ];

  const vpsItems = isAdmin ? [
    { name: 'Overview', href: '/dashboard/admin/vps', icon: Squares2X2Icon },
    { name: 'Automations', href: '/dashboard/admin/vps/automations', icon: PlayIcon },
    { name: 'User profiles', href: '/dashboard/admin/vps/user-profiles', icon: IdentificationIcon },
    { name: 'Performance', href: '/dashboard/admin/vps/performance', icon: ChartBarIcon },
    { name: 'Followback', href: '/dashboard/admin/vps/followback', icon: SignalIcon },
    { name: 'Accounts', href: '/dashboard/admin/vps/accounts', icon: ClipboardDocumentListIcon },
    { name: 'Notif Monitor', href: '/dashboard/admin/vps/notif-health', icon: BellAlertIcon },
    { name: 'Warmup', href: '/dashboard/admin/vps/warmup', icon: SunIcon },
  ] : [];

  // Check if current path is an automation / VPS route
  const isAutomationRoute = automationItems.some(item => location.pathname === item.href);
  const isVpsRoute = location.pathname.startsWith('/dashboard/admin/vps');

  // Auto-open drawers when on matching routes
  useEffect(() => {
    if (isAuthenticated && isAutomationRoute) {
      setAutomationsDrawerOpen(true);
    }
  }, [isAuthenticated, isAutomationRoute, location.pathname]);

  useEffect(() => {
    if (isAuthenticated && isVpsRoute) {
      setVpsDrawerOpen(true);
    }
  }, [isAuthenticated, isVpsRoute, location.pathname]);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600 dark:text-gray-400">Loading...</div>
      </div>
    );
  }

  // Don't render layout if not authenticated - redirect will happen via useEffect
  if (!isAuthenticated) {
    return null;
  }

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'Models', href: '/dashboard/models', icon: UserIcon },
    ...(isAdmin || features.canAddAccounts ? [{ name: 'Accounts', href: '/dashboard/accounts', icon: UserGroupIcon }] : []),
    ...(isAdmin || features.canAddAccounts ? [{ name: 'Chatbot', href: '/dashboard/chatbot', icon: ChatBubbleOvalLeftEllipsisIcon }] : []),
    ...(isAdmin || features.canAddAccounts ? [{ name: 'Chat links', href: '/dashboard/chat-links', icon: LinkIcon }] : []),
    ...(isSuperAdmin ? [{ name: 'Snapchat', href: '/dashboard/snapchat', icon: SnapGhostIcon }] : []),
    { name: 'Proxies', href: '/dashboard/proxies', icon: GlobeAltIcon },
    ...(isAdmin ? [{ name: 'Bart Proxies', href: '/dashboard/bart-proxies', icon: KeyIcon }] : []),
    ...(isSuperAdmin ? [{ name: 'Scraper', href: '/dashboard/scraper', icon: MagnifyingGlassIcon }] : []),
    ...(isAdmin || features.canUseScraper ? [{ name: 'Scraper V2', href: '/dashboard/scraper-v2', icon: MagnifyingGlassIcon }] : []),
    ...(automationItems.length > 0 ? [{ name: 'Automations', href: '#', icon: SparklesIcon, isGroup: true, groupId: 'automations', children: automationItems }] : []),
    ...(vpsItems.length > 0 ? [{ name: 'VPS', href: '#', icon: ComputerDesktopIcon, isGroup: true, groupId: 'vps', children: vpsItems }] : []),
    { name: 'Analytics', href: '/dashboard/analytics', icon: ChartBarIcon },
    ...(user?.role !== 'admin' ? [{ name: 'API', href: '/dashboard/api', icon: CodeBracketIcon }] : []),
    ...(user?.role !== 'admin' ? [{ name: 'Billing', href: '/dashboard/billing', icon: CreditCardIcon }] : []),
    ...(isAdmin ? [
      { name: 'Settings', href: '/dashboard/settings', icon: CogIcon },
      { name: 'Admin', href: '/dashboard/admin/users', icon: ShieldCheckIcon },
    ] : []),
    ...(isSuperAdmin ? [
      { name: 'AI Settings', href: '/dashboard/ai-settings', icon: CpuChipIcon },
      { name: 'Virtual Assistants', href: '/dashboard/admin/vas', icon: IdentificationIcon },
      { name: 'Followback Comparison', href: '/dashboard/admin/followback-profile-comparison', icon: ScaleIcon },
    ] : []),
    ...(isSuperAdmin ? [
      { name: 'Orbex Licenses', href: '/dashboard/admin/orbex-licenses', icon: KeyIcon },
    ] : []),
    ...(user?.username === 'superadmin_wingr' ? [
      { name: 'Orbex Accounts', href: '/dashboard/admin/orbex-accounts', icon: KeyIcon },
    ] : []),
  ];

  const renderNavGroup = (item, { mobile = false } = {}) => {
    const open = item.groupId === 'vps' ? vpsDrawerOpen : automationsDrawerOpen;
    const toggle = item.groupId === 'vps'
      ? () => setVpsDrawerOpen((v) => !v)
      : () => setAutomationsDrawerOpen((v) => !v);
    const active = item.groupId === 'vps' ? isVpsRoute : isAutomationRoute;
    const children = item.children || [];

    return (
      <div key={item.name}>
        <button
          type="button"
          onClick={toggle}
          className={`group w-full flex items-center justify-between px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
            active || open
              ? isDarkMode
                ? 'bg-gradient-to-r from-blue-500/20 to-sky-500/20 text-white border border-blue-500/30'
                : 'bg-gradient-to-r from-blue-500/10 to-sky-500/10 text-blue-700 border border-blue-200'
              : isDarkMode
                ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center">
            <item.icon className="mr-3 h-5 w-5" />
            {item.name}
          </div>
          <ChevronRightIcon className={`h-4 w-4 transition-transform duration-200 ${open ? 'rotate-90' : ''}`} />
        </button>
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className={`${mobile ? 'pl-2' : 'pl-4'} pt-1 space-y-1`}>
            {children.map((child) => (
              <Link
                key={child.name}
                to={child.href}
                onClick={mobile ? () => setSidebarOpen(false) : undefined}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  location.pathname === child.href
                    ? isDarkMode
                      ? 'bg-gradient-to-r from-blue-500/20 to-sky-500/20 text-white border border-blue-500/30'
                      : 'bg-gradient-to-r from-blue-500/10 to-sky-500/10 text-blue-700 border border-blue-200'
                    : isDarkMode
                      ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                      : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'
                }`}
              >
                <child.icon className="mr-3 h-5 w-5" />
                {child.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 glass-overlay" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex w-64 flex-col glass-sidebar">
          <div className="flex h-20 items-center justify-between px-4">
            <div className="flex items-center">
              <WingrLockup size={32} />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              if (item.isGroup) return renderNavGroup(item, { mobile: true });
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    location.pathname === item.href
                      ? isDarkMode
                        ? 'bg-gradient-to-r from-blue-500/20 to-sky-500/20 text-white border border-blue-500/30'
                        : 'bg-gradient-to-r from-blue-500/10 to-sky-500/10 text-blue-700 border border-blue-200'
                      : isDarkMode
                        ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                        : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow glass-sidebar">
          <div className="flex h-20 items-center px-4">
            <div className="flex items-center">
              <WingrLockup size={32} />
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4 overflow-y-auto">
            {navigation.map((item) => {
              if (item.isGroup) return renderNavGroup(item);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    location.pathname === item.href
                      ? isDarkMode
                        ? 'bg-gradient-to-r from-blue-500/20 to-sky-500/20 text-white border border-blue-500/30'
                        : 'bg-gradient-to-r from-blue-500/10 to-sky-500/10 text-blue-700 border border-blue-200'
                      : isDarkMode
                        ? 'text-gray-300 hover:bg-white/10 hover:text-white'
                        : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 glass-nav px-4 sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          {/* User info and actions */}
          <div className="ml-auto flex items-center gap-4">
            {/* Accounts and Conversations counts */}
            {user && (
              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">
                  {user.limits?.maxAccounts === 0 || user.limits?.maxAccounts === undefined || user.limits?.maxAccounts === null
                    ? `${accountsCount}/unlimited Accounts` 
                    : `${accountsCount}/${user.limits.maxAccounts} Accounts`}
                </span>
                <span className="font-medium">
                  {conversationsRemaining.toLocaleString()} conversations remaining
                </span>
              </div>
            )}
            {user && (
              <span className="text-sm text-gray-600 dark:text-gray-300">
                {user.name || user.username}
              </span>
            )}
            {/* Superadmin: view-as-user switcher */}
            {user?.role === 'superadmin' && <ViewingAsSwitcher />}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              title="Logout (automations will continue running)"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
              Logout
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <SunIcon className="h-5 w-5" />
              ) : (
                <MoonIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
